import { ArdoqId } from '@ardoq/api-types';
import { activeScenarioOperations } from '@ardoq/core';
import { ActiveScenarioState } from '@ardoq/data-model';
import {
  type FeatureContext,
  featureOperations,
  hasAIGeneratedScenariosFeature,
  hasScenarioFeature,
} from '@ardoq/features';
import {
  PermissionContext,
  canDo,
  permissionsOperations,
} from '@ardoq/access-control';

/**
 * Check if the current user has the correct access to
 * create a new scenario (checks for privileges and feature).
 * @returns {boolean}
 */
const canCreateScenario = (
  permissionContext: PermissionContext,
  featureContext: FeatureContext
): boolean =>
  featureOperations.hasScenarioFeature(featureContext) &&
  permissionsOperations.isLoadedPermissionContext(permissionContext) &&
  canDo({
    action: 'organization/create_scenario',
    permissionContext,
    resourceId: permissionContext.user.organization._id,
  });

/**
 * Check if the current user may add components to an existing scenario
 * (checks for the feature and write access).
 * @returns {boolean}
 */
const canAddToScenario = (
  permissionContext: PermissionContext,
  featureContext: FeatureContext
): boolean =>
  featureOperations.hasScenarioFeature(featureContext) &&
  permissionsOperations.isLoadedPermissionContext(permissionContext) &&
  permissionContext.user.writeAccess;

const canAdminScenario = (
  permissionContext: PermissionContext,
  scenarioId: ArdoqId
): boolean => {
  return (
    // AI generated scenarios will be exposed to non-scenario orgs
    (hasScenarioFeature() || hasAIGeneratedScenariosFeature()) &&
    canDo({
      action: 'scenario/admin',
      permissionContext,
      resourceId: scenarioId,
    })
  );
};

const canEditScenario = (
  permissionContext: PermissionContext,
  scenarioId: ArdoqId
): boolean => {
  return (
    (hasScenarioFeature() || hasAIGeneratedScenariosFeature()) &&
    canDo({
      action: 'scenario/edit',
      permissionContext,
      resourceId: scenarioId,
    })
  );
};

const canEditActiveScenario = (
  permissionContext: PermissionContext,
  activeScenarioState: ActiveScenarioState
): boolean => {
  const activeScenarioId =
    activeScenarioOperations.getActiveScenarioId(activeScenarioState);

  const isInDiffMode = activeScenarioOperations.scenarioIsInDiffMode(
    activeScenarioState,
    activeScenarioId
  );

  return !isInDiffMode && canEditScenario(permissionContext, activeScenarioId);
};

export const scenarioAccessControlInterface = {
  canCreateScenario,
  canAddToScenario,
  canAdminScenario,
  canEditScenario,
  canEditActiveScenario,
};
